import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { platformApi } from '../api'
import type { Answers, FormDefinition } from '../types'
import { activePages, fieldIndex, missingRequired } from '../lib/form'
import FormRenderer from './FormRenderer'

const MIN_REASON = 5

interface Props {
    open: boolean
    onOpenChange: (o: boolean) => void
    submissionId: string
    definition: FormDefinition
    answers: Answers
    onSaved: () => void
}

/** Lets the current reviewers correct a submitted request. Every save needs a reason and is audited. */
export function EditAnswersSheet({ open, onOpenChange, submissionId, definition, answers, onSaved }: Props) {
    const [draft, setDraft] = useState<Answers>(answers)
    const [reason, setReason] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const index = useMemo(() => fieldIndex(definition), [definition])
    const changes = useMemo(() => {
        const out: Record<string, unknown> = {}
        for (const k of new Set([...Object.keys(answers), ...Object.keys(draft)])) {
            if (index[k]?.type === 'file') continue
            if (JSON.stringify(answers[k] ?? null) !== JSON.stringify(draft[k] ?? null)) out[k] = draft[k] ?? null
        }
        return out
    }, [answers, draft, index])
    const missing = missingRequired(definition, draft)
    const canSave = Object.keys(changes).length > 0 && reason.trim().length >= MIN_REASON && missing.length === 0 && !busy

    const reset = () => { setDraft(answers); setReason(''); setError(null) }
    const save = async () => {
        setBusy(true)
        setError(null)
        try {
            await platformApi.reviewEdit(submissionId, changes, reason.trim())
            toast.success('Changes saved and recorded in the history')
            onOpenChange(false)
            onSaved()
        } catch (e) {
            setError(e instanceof ApiError && e.problems.length ? e.problems.join(' ') : e instanceof Error ? e.message : 'Could not save')
        } finally {
            setBusy(false)
        }
    }

    return (
        <Sheet open={open} onOpenChange={(o) => { if (!busy) { onOpenChange(o); if (!o) reset() } }}>
            <SheetContent className="w-full data-[side=right]:sm:max-w-2xl">
                <SheetHeader>
                    <SheetTitle>Edit submitted answers</SheetTitle>
                    <SheetDescription>
                        Your changes, your name and the reason are saved in the request's history, and the submitter is notified.
                        Attached files can't be changed.
                    </SheetDescription>
                </SheetHeader>
                <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-4 pb-4">
                    {activePages(definition, draft).map((p) => {
                        const page = { ...p, fields: p.fields.filter((f) => f.type !== 'file') }
                        if (page.fields.length === 0) return null
                        return (
                            <section key={p.id} className="flex flex-col gap-3">
                                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{p.title}</h3>
                                <FormRenderer page={page} answers={draft} invalidKeys={missing}
                                    onChange={(k, v) => setDraft((d) => ({ ...d, [k]: v }))} />
                            </section>
                        )
                    })}
                    <Field>
                        <FieldLabel htmlFor="edit-reason">Why are you changing this?</FieldLabel>
                        <Textarea id="edit-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
                        <FieldDescription className="text-xs">Required. Visible in the history.</FieldDescription>
                    </Field>
                    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
                </div>
                <SheetFooter>
                    <Button type="button" variant="outline" disabled={busy} onClick={() => { onOpenChange(false); reset() }}>Cancel</Button>
                    <Button type="button" disabled={!canSave} onClick={save}>
                        {busy && <Spinner data-icon="inline-start" />}Save changes
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    )
}
